/*
  Kkumi Earn Points — shared client for every "you'll earn X points" tracker.

  The effective points-per-dollar = the rewards app's earn rate from
  /apps/rewards/program × the signed-in customer's tier multiplier from
  /apps/rewards/balance, × the best live campaign multiplier (+ any flat
  campaign bonus) from /apps/rewards/campaigns. All three are App Proxy URLs
  signed by Shopify, so they're the single source of truth.

  Campaign effect mirrors the orders/paid webhook exactly: the best multiplier
  wins (they don't stack), flat bonuses sum, and each only applies when the
  previewed (earn-eligible) total meets that campaign's min_order_cents. So a
  live "2× points this weekend" shows doubled points everywhere a base number
  used to show.

  Currency: every total handed in here (Liquid prices, /cart.js, FBT sums) is
  in the shopper's currency, but the earn rate, tier spend and campaign
  minimums are per SHOP-currency dollar, and orders/paid credits the
  shop-currency subtotal. So the maths runs on KKMoney.toShopCents(total) —
  without it a ¥2,500 kit previewed 2,500 "dollars" of points. If the amount
  can't be converted the tracker stays loading rather than show a guess.

  Scopes:
    cart    — total is cart.total_price (in cents); refreshed on cart mutations.
    product — total is the selected variant price; refreshed on PUB_SUB_EVENTS.variantChange.
    bundle  — total is updated by the host section via KkumiEarnPoints.setTotal(el, cents).

  Until both endpoints resolve every tracker stays in a loading state — we
  never render a "fallback" number that might be wrong by a tier multiple.
*/
window.KkumiEarnPoints = (function () {
  // The Polco Points coin badge, shown immediately before every previewed
  // number. Lockstep with snippets/kkumi-polco-coin.liquid (the Liquid twin) —
  // change one, change the other. Sizing/layout live in kkumi-earn-points.css.
  var COIN_SVG = '<svg class="kkumi-earn-points__coin" viewBox="6 6 108 108" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><circle cx="62" cy="64" r="48" fill="#000" opacity="0.16"/><circle cx="58" cy="56" r="48" fill="#c94a7b"/><circle cx="58" cy="56" r="42" fill="#f279a1"/><circle cx="58" cy="56" r="41" fill="none" stroke="#fff" stroke-opacity="0.9" stroke-width="3.6" stroke-dasharray="0 8.6" stroke-linecap="round"/><path d="M58.0,29.0 L64.7,49.3 L85.0,56.0 L64.7,62.7 L58.0,83.0 L51.3,62.7 L31.0,56.0 L51.3,49.3 Z" fill="#fff"/></svg>';

  var trackers = [];
  var ratePromise = null;
  var rate = null; // { perDollar, multiplier } once resolved
  var lastCartTotal = null;
  var cartListenersInstalled = false;

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchRate() {
    if (ratePromise) return ratePromise;
    var base = shopifyRoot().replace(/\/$/, '');
    // Read through the shared client cache (window.KKRewards) when present so
    // /program + /campaigns (+ the /balance version probe) aren't refetched on
    // every product/cart page when nothing changed; fall back to a direct fetch.
    function rget(path, scope) {
      if (window.KKRewards) return window.KKRewards.get(path, { scope: scope }).catch(function () { return null; });
      return fetch(base + '/apps/rewards' + path, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    }
    ratePromise = Promise.all([
      rget('/program', 'public'),
      rget('/balance', 'member'),
      // Live campaigns are non-fatal: if this fails we still preview the base
      // (tier-adjusted) points rather than blocking the tracker.
      rget('/campaigns', 'public')
    ]).then(function (res) {
      var prog = res[0], bal = res[1], camp = res[2];
      var perDollar = prog && prog.points && Number(prog.points.earn_per_dollar);
      if (!Number.isFinite(perDollar) || perDollar <= 0) return null;
      var mult = bal && bal.tier && Number(bal.tier.multiplier);
      var excluded = (prog && prog.excluded_variant_ids) || [];
      rate = {
        perDollar: perDollar,
        multiplier: (Number.isFinite(mult) && mult > 0) ? mult : 1,
        // Tier NAME, so the rate line can name the rate that's applying
        // ("15 pts per $1 · Idol rate") instead of leaving the shopper to
        // reconcile it against a footer quoting the base rate.
        tierName: (bal && bal.tier && bal.tier.name) || '',
        // Club gate: signed in but not in the Kkumi Club → no points earned,
        // so trackers show the join prompt instead. An older worker without
        // the field reads as a member.
        notClub: !!(bal && bal.logged_in && bal.club_member === false),
        excluded: Array.isArray(excluded) ? excluded.map(String) : [],
        // `signup_bonus` fallback: tolerates a worker still on the
        // pre-2026-07-25 key while the theme is already live.
        secondOrder: (prog && prog.points && Number(prog.points.second_order_bonus != null ? prog.points.second_order_bonus : prog.points.signup_bonus)) || 0,
        // Keep only the point-effect campaigns (a multiplier or a flat bonus);
        // banner-only / discount-only campaigns don't change the earn number.
        campaigns: (camp && Array.isArray(camp.campaigns) ? camp.campaigns : []).filter(function (c) {
          return (Number(c.multiplier) > 1) || (Number(c.bonus_points) > 0);
        })
      };
      // The Liquid seed (cart.total_price) can't know about excluded
      // add-ons; once we do, re-pull the cart so previews are exact.
      if (rate.excluded.length) refreshCartTotal();
      return rate;
    });
    return ratePromise;
  }

  // A storefront amount (shopper's currency, cents) in shop-currency cents —
  // the unit every rule below is written in. Null when it can't be converted.
  // KKMoney loads head-defer before this file; the fallback only covers a page
  // where it didn't, and refuses to treat a foreign amount as the shop's.
  function shopCents(cents) {
    var n = Number(cents) || 0;
    if (window.KKMoney && typeof window.KKMoney.toShopCents === 'function') return window.KKMoney.toShopCents(n);
    var c = window.KKMoneyConfig;
    return (c && c.currency && c.shopCurrency && c.currency !== c.shopCurrency) ? null : n;
  }

  // Best campaign multiplier (they don't stack) + summed flat bonus for a given
  // earn-eligible total in SHOP-currency cents. Each campaign only applies when
  // the total meets its min_order_cents — same gate as the orders/paid webhook.
  function campaignFor(cents) {
    var mult = 1, bonus = 0;
    if (rate && rate.campaigns) {
      for (var i = 0; i < rate.campaigns.length; i++) {
        var c = rate.campaigns[i];
        if (cents < (Number(c.min_order_cents) || 0)) continue;
        var m = Number(c.multiplier);
        if (m > mult) mult = m;
        var b = Number(c.bonus_points);
        if (b > 0) bonus += Math.trunc(b);
      }
    }
    return { mult: mult, bonus: bonus };
  }

  // Points previewed for an earn-eligible total (cents, shopper's currency).
  // Null until the rate resolves, or if the total can't be converted. Mirrors
  // the webhook: floor(shop dollars × perDollar × tier × campaign) then add
  // the flat campaign bonus.
  function pointsFor(cents) {
    if (rate == null) return null;
    var shop = shopCents(cents);
    if (shop == null) return null;
    var base = (shop / 100) * rate.perDollar * rate.multiplier;
    var cf = campaignFor(shop);
    return Math.floor(base * cf.mult) + cf.bonus;
  }

  // The points-per-dollar actually applying to this shopper right now: the
  // program's base rate x their tier multiplier x the best live campaign
  // multiplier for this total. Flat campaign bonuses are deliberately NOT
  // folded in — they're a one-off top-up, not a rate. Null until the rate
  // resolves. Trimmed to at most 2 decimals ("13.5", not "13.500000000001").
  function effectiveRate(cents) {
    if (rate == null) return null;
    var shop = shopCents(cents);
    if (shop == null) return null;
    var eff = rate.perDollar * rate.multiplier * campaignFor(shop).mult;
    return Math.round(eff * 100) / 100;
  }

  // The small "how you're earning" line beside an estimate. Merchant copy
  // (data-rate-pattern) with [rate] -> the effective per-dollar rate and
  // [tier] -> the shopper's tier name. Members whose tier or a campaign lifts
  // them above the base rate are the whole point of it, but it renders for
  // everyone the pattern is set on, so a guest sees the rate they'd be on.
  function renderRate(t, totalCents) {
    if (!t.rateEl) return;
    var eff = effectiveRate(totalCents);
    if (eff == null) { t.rateEl.textContent = ''; return; }
    // Tier name is API-controlled, so it is escaped before entering innerHTML.
    // If the shared escaper somehow hasn't loaded, drop the name rather than
    // throw (which would take the points estimate down with it).
    var tierName = (rate && rate.tierName) || '';
    var out = t.ratePattern
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .split('[rate]').join(eff.toLocaleString())
      .split('[tier]').join(window.KKEsc ? window.KKEsc(tierName) : '');
    // A guest (and a member whose /balance came back without a tier) has no
    // tier name, which leaves the pattern's qualifier clause dangling —
    // "15 pts per $1 ·". Collapse the whitespace the empty token left behind
    // and drop a now-trailing separator so the line still reads as a sentence.
    if (!tierName) out = out.replace(/\s+/g, ' ').replace(/[·|\-–—,]\s*$/, '').trim();
    t.rateEl.innerHTML = out;
  }

  function compose(pattern, pillHtml) {
    // Pattern is a merchant-controlled inline_richtext setting (sanitised by
    // Shopify to a safe tag allow-list before it ever reaches us), so render
    // it raw to preserve <a>, <strong>, etc. Transform **bold** → <strong>,
    // inject the points pill, then fill the live second-order bonus from
    // /program ([second_order], or the legacy [signup] name — never a
    // hardcoded number; blank until the program loads).
    var html = pattern.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    var bonus = (rate && rate.secondOrder) ? '<strong>' + Number(rate.secondOrder).toLocaleString() + '</strong>' : '…';
    return html.replace('[points]', pillHtml).replace(/\[second_order\]|\[signup\]/g, bonus);
  }

  function renderTracker(t) {
    var copy = t.copyEl;
    if (!copy) return;
    var totalCents = (typeof t.getTotal === 'function') ? (t.getTotal() | 0) : 0;
    var pts = pointsFor(totalCents);
    renderRate(t, totalCents);
    if (pts == null) {
      // Loading: coin + a shimmer bar in place of the number (never a bare
      // "…", which reads as broken copy — "You'll earn … Polco Points").
      t.el.classList.add('is-loading');
      var loadingPill = '<span class="kkumi-earn-points__pill is-loading" aria-busy="true">' +
        (t.showCoin ? COIN_SVG : '') + '<span class="kk-skel kkumi-earn-points__count-skel" aria-hidden="true"></span></span>';
      copy.innerHTML = compose(t.pattern, loadingPill);
      return;
    }
    t.el.classList.remove('is-loading');
    if (rate && rate.notClub && t.isCustomer && t.joinPattern && window.KKEsc) {
      // The number stays — it's what joining would earn them on this order —
      // but the whole line becomes the join link. Merchant text is plain
      // `text`, so it is escaped (KKEsc, head-deferred before this file)
      // before the pill is injected.
      if (t.rateEl) t.rateEl.textContent = '';
      var esc = window.KKEsc;
      var joinPill = '<span class="kkumi-earn-points__pill">' + (t.showCoin ? COIN_SVG : '') +
        '<span class="kkumi-earn-points__count ' + t.countClass + '">' + pts + '</span></span>';
      var joinText = esc(t.joinPattern).split('[points]').join(joinPill);
      var joinLink = '<a class="kkumi-earn-points__join" href="' + esc(t.joinUrl) + '">' +
        (t.joinLinkLabel ? esc(t.joinLinkLabel) : joinText) + '</a>';
      copy.innerHTML = t.joinLinkLabel ? joinText + ' ' + joinLink : joinLink;
      return;
    }
    if (pts === 0 && t.isCustomer && t.zeroPattern) {
      copy.innerHTML = t.zeroPattern.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      return;
    }
    var pill = '<span class="kkumi-earn-points__pill">' + (t.showCoin ? COIN_SVG : '') +
      '<span class="kkumi-earn-points__count ' + t.countClass + '">' + pts + '</span></span>';
    copy.innerHTML = compose(t.pattern, pill);
  }

  function renderAll() { trackers.forEach(renderTracker); }
  function renderScope(scope) {
    trackers.forEach(function (t) { if (t.scope === scope) renderTracker(t); });
  }

  // Points are earned on the discounted subtotal minus excluded add-ons
  // (e.g. signature on delivery) — mirror that in the preview. cart.total_price
  // already reflects discounts; subtract the excluded lines' final prices.
  function eligibleCartCents(cart) {
    var total = cart.total_price;
    if (rate && rate.excluded.length && cart.items) {
      cart.items.forEach(function (item) {
        if (rate.excluded.indexOf(String(item.variant_id)) !== -1) {
          total -= item.final_line_price;
        }
      });
    }
    return total < 0 ? 0 : total;
  }

  function refreshCartTotal() {
    if (!trackers.some(function (t) { return t.scope === 'cart'; })) return;
    fetch(shopifyRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastCartTotal = eligibleCartCents(cart);
        renderScope('cart');
        notifyCartTotal();
      }).catch(function () {});
  }

  // Let other widgets (e.g. the cart wallet's tier-progress projection) react
  // to the eligible cart total without re-fetching /cart.js themselves.
  function notifyCartTotal() {
    try {
      document.dispatchEvent(new CustomEvent('kkumi:cart-total', { detail: { cents: cartCents() } }));
    } catch (e) { /* CustomEvent unsupported — getter still works */ }
  }

  function installCartListeners() {
    if (cartListenersInstalled) return;
    cartListenersInstalled = true;
    var debounce;
    function schedule() { clearTimeout(debounce); debounce = setTimeout(refreshCartTotal, 350); }

    var form = document.querySelector('form[action^="/cart"]');
    if (form) new MutationObserver(schedule).observe(form, { childList: true, subtree: true });
    var contents = document.querySelector('.js-contents');
    if (contents) new MutationObserver(schedule).observe(contents, { childList: true, subtree: true });
    document.addEventListener('cart:refresh', schedule);
    document.addEventListener('cart:added', schedule);

    // Dawn's cartUpdate pub/sub carries the freshly-rendered cart (with
    // total_price + items), so we can update INSTANTLY from it — no debounced
    // /cart.js round-trip. This is what keeps the wallet's tier projection in
    // lock-step with the cart the way the free-shipping bar is (it also reads
    // cartUpdate). Falls back to the debounced fetch if the payload is thin.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          var cart = event && event.cartData;
          if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
            lastCartTotal = eligibleCartCents(cart);
            renderScope('cart');
            notifyCartTotal();
          } else {
            schedule();
          }
        });
      }
    } catch (e) { /* pub/sub unavailable — observers + custom events still fire */ }
  }

  function installProductListener(t) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var v = event && event.data && event.data.variant;
      if (!v || typeof v.price !== 'number') return;
      var sectionId = t.el.dataset.sectionId;
      // If the event carries a section id, only the matching tracker reacts.
      // Otherwise every product tracker on the page updates (single-PDP case).
      if (sectionId && event.data.sectionId && sectionId !== event.data.sectionId) return;
      t.currentPriceCents = v.price;
      renderTracker(t);
    });
  }

  function register(el) {
    if (el.__kkumiEarnRegistered) return;
    el.__kkumiEarnRegistered = true;

    var t = {
      el: el,
      copyEl: el.querySelector('.kkumi-earn-points__copy'),
      scope: el.dataset.scope || 'cart',
      pattern: el.dataset.pattern || "You'll earn [points] Polco Points.",
      zeroPattern: el.dataset.zeroPattern || '',
      isCustomer: el.dataset.isCustomer === 'true',
      joinPattern: el.dataset.joinPattern || '',
      joinLinkLabel: el.dataset.joinLinkLabel || '',
      joinUrl: el.dataset.joinUrl || '/pages/kkumi-club#kkumi-club-join',
      showCoin: el.dataset.showCoin !== 'false',
      countClass: el.dataset.countClass || '',
      ratePattern: el.dataset.ratePattern || '',
      rateEl: el.querySelector('[data-kk-earn-rate]')
    };

    if (t.scope === 'cart') {
      var seedCart = parseInt(el.dataset.initialTotalCents, 10);
      if (Number.isFinite(seedCart) && lastCartTotal == null) lastCartTotal = seedCart;
      t.getTotal = function () { return lastCartTotal == null ? 0 : lastCartTotal; };
      installCartListeners();
    } else if (t.scope === 'product') {
      t.currentPriceCents = parseInt(el.dataset.variantPrice, 10) || 0;
      t.getTotal = function () { return t.currentPriceCents; };
      installProductListener(t);
    } else if (t.scope === 'bundle') {
      var seedBundle = parseInt(el.dataset.initialTotalCents, 10);
      t.currentTotalCents = Number.isFinite(seedBundle) ? seedBundle : 0;
      t.getTotal = function () { return t.currentTotalCents; };
    }

    trackers.push(t);
    renderTracker(t);
    fetchRate().then(function () { renderTracker(t); });
  }

  function setTotal(el, cents) {
    var t = null;
    for (var i = 0; i < trackers.length; i++) { if (trackers[i].el === el) { t = trackers[i]; break; } }
    if (!t || t.scope !== 'bundle') return;
    t.currentTotalCents = cents | 0;
    renderTracker(t);
  }

  function scan(root) {
    (root || document).querySelectorAll('.kkumi-earn-points[data-scope]').forEach(register);
  }

  function init() {
    // Pill / coin / skeleton styling lives in assets/kkumi-earn-points.css
    // (loaded globally), so it's parsed before first paint — no JS injection.
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-scan when sections re-render in the theme editor or via AJAX.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target || document); });

  // For consumers that render their own UI (e.g. the JS-built bundle block):
  // returns the same tier-adjusted points number every snippet tracker shows,
  // resolving once the rewards app has responded. `cents` is a storefront
  // amount (shopper's currency), like every other total handed to this
  // module — the shop-currency conversion happens here. Synchronous compute() is
  // also exposed for code that already has a render loop and can re-call.
  function compute(cents) {
    return pointsFor(cents);
  }
  function computeAsync(cents) {
    return fetchRate().then(function () { return compute(cents); });
  }

  // Eligible cart total — what counts toward points/tier spend — in SHOP-
  // currency cents, the unit tier thresholds are in. Null until the
  // seed/first fetch resolves, or when it can't be converted.
  function cartCents() { return lastCartTotal == null ? null : shopCents(lastCartTotal); }

  // The Polco Points coin markup — so runtime-built UIs (the FBT block) show
  // the same badge without re-declaring the SVG.
  function coinHTML() { return COIN_SVG; }

  return { register: register, setTotal: setTotal, refresh: renderAll, scan: scan, compute: compute, computeAsync: computeAsync, cartCents: cartCents, coinHTML: coinHTML, effectiveRate: effectiveRate };
})();

